import { useEffect, useState } from 'react';
import camelCase from 'lodash/camelCase';
import { useBusinessEntity } from '@atlassian/jira-business-entity-common/src/controllers/business-entity-context/index.tsx';
import { useLocalStorageWithExpiry } from '@atlassian/jira-insights-common/src/services/use-local-storage/main.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useQueryParam } from '@atlassian/react-resource-router';
import { isKebabCase } from '../../common/kebab-case/index.tsx';
import { DAY_IN_MS, NAMESPACE_PREFIX } from './constants.tsx';

/*
 * Reads from query string first, then falls back to local storage
 * If query string and local storage are different, updates local storage
 *
 * Note: convention for namespacing in local storage is kebab case
 * where as convention for namespacing in query string is camel case
 */
export const useStatisticsStorage = (
	namespace: string, // must be in kebab-case
): [string | undefined, (selectedFieldId: string | undefined) => void] => {
	const accountId = useAccountId();
	const { data: entity } = useBusinessEntity();

	if (isKebabCase(namespace) === false) {
		throw new Error('localStorageNamespace must be in kebab-case');
	}

	const queryParamNamespace = camelCase(namespace);

	// read from query string
	const [queryValue] = useQueryParam(queryParamNamespace);
	const [initialQueryValue, setInitialQueryValue] = useState(queryValue);

	// read from local storage
	const [localValue, setLocalValue] = useLocalStorageWithExpiry<string | undefined>(
		`${NAMESPACE_PREFIX}-${namespace}`,
		`${accountId}-${entity.id}`,
		DAY_IN_MS * 30,
	);

	/*
	 * If query string value is present AND is different from local storage value, update local storage

	 * Note: Because the query string update lags behind, we need to use a ref to ensure
	 * when selectedFieldId is changed in the UI it doesn't immediately revert back to
	 * the previous value, because the query string update lags behind
	 */
	useEffect(() => {
		if (initialQueryValue) {
			setLocalValue(initialQueryValue);
			setInitialQueryValue(undefined);
		}
	}, [initialQueryValue, setLocalValue]);

	return [initialQueryValue ?? localValue, setLocalValue];
};
